import * as React from "react";
import clsx from "clsx";
import { User, LogOut, Languages } from "lucide-react";
import { useT } from "../../../app/lib/i18n";
import { Avatar } from "../Avatar";
import { normalizeAvatarUrl } from "../ImageUpload/logic/avatarImageUtils";
import { baseTheme } from "../../styles/baseTheme";
import * as s from "./UserMenu.style.css";
import type { UserMenuProps, UserMenuHandle } from "./UserMenu.types";

const MOBILE_BREAKPOINT_PX = parseInt(baseTheme.breakpoints.md, 10);

export type { NavUser, UserMenuProps, UserMenuHandle, UserMenuDropdownDirection } from "./UserMenu.types";

const ChevronDownIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const EmailAlertIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

/**
 * Profile-avatar trigger with a Profile/Log out dropdown. Fully self-contained
 * (open state, outside-click handling, positioning) so it can be dropped into
 * any layout — a sidebar footer (NavigationBar) or a plain header
 * (CertificationShell) alike.
 *
 * A parent that manages sibling dropdowns (e.g. NavigationBar's org-context
 * switcher) can force this one closed via the forwarded ref, and can react to
 * this one opening via `onOpenChange` to close its own siblings in turn.
 */
export const UserMenu = React.forwardRef<UserMenuHandle, UserMenuProps>(function UserMenu(
  {
    user,
    sidebarOpen = true,
    dropdownDirection = "down",
    dropdownMinWidth = 180,
    compactOnMobile = false,
    onActionComplete,
    onOpenChange,
  },
  ref,
) {
  const t = useT();
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const [triggerRect, setTriggerRect] = React.useState<DOMRect | null>(null);
  const isOpen = triggerRect !== null;

  const close = React.useCallback(() => setTriggerRect(null), []);
  const open = React.useCallback(() => {
    if (!triggerRef.current) return;
    setTriggerRect(triggerRef.current.getBoundingClientRect());
  }, []);

  React.useImperativeHandle(ref, () => ({ close }), [close]);

  const onOpenChangeRef = React.useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;
  React.useEffect(() => {
    onOpenChangeRef.current?.(isOpen);
  }, [isOpen]);

  React.useEffect(() => {
    if (!isOpen) return;
    const handlePointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      const inMenu =
        triggerRef.current?.contains(target) || dropdownRef.current?.contains(target);
      if (!inMenu) close();
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [isOpen, close]);

  const getDropdownStyle = React.useCallback((): React.CSSProperties => {
    if (!triggerRect) return {};
    if (
      compactOnMobile &&
      typeof window !== "undefined" &&
      window.innerWidth <= MOBILE_BREAKPOINT_PX
    ) {
      // Span the viewport, anchored just below the trigger, instead of the
      // normal left-anchored min-width box.
      return { top: triggerRect.bottom + 4, left: 0, right: 0, minWidth: 0 };
    }
    if (dropdownDirection === "down") {
      return { top: triggerRect.bottom + 4, left: triggerRect.left, minWidth: dropdownMinWidth };
    }
    return {
      bottom: window.innerHeight - triggerRect.top + 4,
      left: triggerRect.left,
      minWidth: dropdownMinWidth,
    };
  }, [triggerRect, dropdownDirection, dropdownMinWidth, compactOnMobile]);

  const handleTrigger = () => {
    if (isOpen) close();
    else open();
  };

  const runAction = (fn?: () => void) => () => {
    fn?.();
    close();
    onActionComplete?.();
  };

  const languageLabel =
    user.languageName?.trim() ||
    user.changeLanguageLabel ||
    "English";

  return (
    <div className={s.root}>
      <button
        ref={triggerRef}
        type="button"
        className={s.trigger}
        onClick={handleTrigger}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        title={!sidebarOpen ? user.name : undefined}
      >
        {user.firstName != null && user.lastName != null ? (
          <Avatar
            email={user.email ?? ""}
            firstName={user.firstName}
            lastName={user.lastName}
            image={normalizeAvatarUrl(user.avatarUrl) || undefined}
            size={32}
          />
        ) : (
          <span className={s.avatar} aria-hidden>
            {user.initials ?? user.name.slice(0, 2).toUpperCase()}
          </span>
        )}
        {sidebarOpen && (
          <>
            <span className={clsx(s.info, compactOnMobile && s.mobileHidden)}>
              <span className={s.name}>{user.name}</span>
              {user.email && (
                <span className={s.emailRow}>
                  <span
                    className={clsx(
                      s.email,
                      user.emailVerified === false && s.emailUnverified,
                    )}
                  >
                    {user.email}
                  </span>
                  {user.emailVerified === false && user.onUnverifiedEmailClick && (
                    <span
                      role="button"
                      tabIndex={0}
                      className={s.emailAlert}
                      title={t("shared.userMenu.emailNotVerified")}
                      aria-label={t("shared.userMenu.emailNotVerified")}
                      onClick={(e) => {
                        e.stopPropagation();
                        user.onUnverifiedEmailClick?.();
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          e.stopPropagation();
                          user.onUnverifiedEmailClick?.();
                        }
                      }}
                    >
                      <EmailAlertIcon />
                    </span>
                  )}
                </span>
              )}
            </span>
            <span className={clsx(s.chevron, compactOnMobile && s.mobileHidden)}>
              <ChevronDownIcon />
            </span>
          </>
        )}
      </button>

      {isOpen && (
        <div
          ref={dropdownRef}
          className={s.dropdown}
          style={getDropdownStyle()}
          role="menu"
        >
          {user.onAccountClick && (
            <button
              type="button"
              role="menuitem"
              className={s.dropdownItem}
              onClick={runAction(user.onAccountClick)}
            >
              <span className={s.dropdownItemIcon} aria-hidden>
                <User size={16} strokeWidth={1.75} />
              </span>
              {user.profileLabel ?? "Profile"}
            </button>
          )}
          {user.onChangeLanguageClick && (
            <button
              type="button"
              role="menuitem"
              className={s.dropdownItem}
              onClick={runAction(user.onChangeLanguageClick)}
            >
              {user.languageFlagUrl ? (
                <img
                  src={user.languageFlagUrl}
                  alt=""
                  className={s.languageFlag}
                />
              ) : (
                <span className={s.dropdownItemIcon} aria-hidden>
                  <Languages size={16} strokeWidth={1.75} />
                </span>
              )}
              {languageLabel}
            </button>
          )}
          {(user.onAccountClick || user.onChangeLanguageClick) && user.onLogoutClick && (
            <div className={s.dropdownDivider} />
          )}
          {user.onLogoutClick && (
            <button
              type="button"
              role="menuitem"
              className={clsx(s.dropdownItem, s.dropdownItemDanger)}
              onClick={runAction(user.onLogoutClick)}
            >
              <span className={s.dropdownItemIcon} aria-hidden>
                <LogOut size={16} strokeWidth={1.75} />
              </span>
              {user.logOutLabel ?? "Log out"}
            </button>
          )}
        </div>
      )}
    </div>
  );
});
